import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider 
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface UserSettings {
  notifications_enabled: boolean;
  api_key: string;
  webhook_url: string;
}

const PublisherSettings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>({
    notifications_enabled: true,
    api_key: '',
    webhook_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // This would normally fetch real settings from the API, but we'll use mock data for now
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        // Normally, we'd fetch from the API
        // For demo, we'll use mock data
        setTimeout(() => {
          setSettings({
            notifications_enabled: true,
            api_key: `pk_live_${user?.publisher_id?.substring(0, 8) || 'xxxxxxxx'}`,
            webhook_url: ''
          });
          setLoading(false);
        }, 1000);
      } catch (err) {
        setError('Failed to load settings. Please try again later.');
        setLoading(false);
      }
    };
    
    if (user?.publisher_id) {
      fetchSettings();
    } else {
      setLoading(false);
      setError('Please log in to access your settings');
    }
  }, [user]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setSettings({
      ...settings,
      [name]: e.target.type === 'checkbox' ? checked : value
    });
  };
  
  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // This would normally send the settings to the API
      // For demo, we'll simulate a successful update
      setTimeout(() => {
        setSuccess('Settings updated successfully');
        setSaving(false);
      }, 1000);
    } catch (err) {
      setError('Failed to save settings. Please try again later.');
      setSaving(false);
    }
  };
  
  const regenerateApiKey = () => {
    if (confirm('Are you sure you want to regenerate your API key? This will invalidate your existing key.')) {
      setSaving(true);
      // For demo, we'll simulate a key regeneration
      setTimeout(() => {
        setSettings({
          ...settings,
          api_key: `pk_live_${Math.random().toString(36).substring(2, 10)}`
        });
        setSuccess('API key regenerated successfully');
        setSaving(false);
      }, 1000);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Publisher Settings
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Account Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              value={user?.name || ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              value={user?.email || ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Publisher ID"
              value={user?.publisher_id || ''}
              fullWidth
              disabled
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          API Keys & Integration
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="API Key"
              value={settings.api_key}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" color="warning" onClick={regenerateApiKey}>
                Regenerate API Key
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="webhook_url"
              label="Webhook URL (Optional)"
              value={settings.webhook_url}
              onChange={handleInputChange}
              fullWidth
              placeholder="https://"
              helperText="Receive realtime notifications for ad events"
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Notification Preferences
        </Typography>
        <FormControlLabel
          control={
            <Switch
              name="notifications_enabled"
              checked={settings.notifications_enabled}
              onChange={handleInputChange}
              color="primary"
            />
          }
          label="Email Notifications"
        />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Receive email notifications about ad performance, integration updates and new features.
        </Typography>
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={saveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default PublisherSettings; 